import React, { useState } from "react";
import {
  TextField,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  IconButton,
  Popover,
  Box,
  Typography,
  Button,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useTheme } from "@mui/material/styles";

/**
 * ColumnFilter — the in-header filter control for a data-table column (FEAT-031).
 *
 * Three types, driven by the column config's `filterType`:
 *   - "text"   : free-text contains match (ids, names, free text). Value: string.
 *   - "select" : multi-select of the values present in the column (priority, type, category,
 *                status). Value: string[] (empty = no filter).
 *   - "date"   : relative / range / exact date filter for timestamp columns. Value:
 *                { mode: "relative", days } | { mode: "range", start, end } | { mode: "exact", date }.
 *
 * Filtering itself is applied client-side over the loaded rows in dataTableMD.
 */

const inputTextSx = (theme) => ({
  "& .MuiInputBase-input": {
    color: theme.palette.custom?.black || "#222",
    fontSize: "13px",
    padding: "2px 0",
  },
  "& .MuiInputBase-input::placeholder": {
    // gray6 (#5B5B5B) is readable; custom.gray is #E8E8E8 (near-white) and was unreadable.
    color: theme.palette.custom?.gray6 || "#5B5B5B",
    opacity: 1,
  },
});

const ColumnFilter = ({ type, value, onChange, options }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState("");

  if (type === "select") {
    const selected = Array.isArray(value) ? value : [];
    // Options may be plain strings OR { value, label } (label lets a column show friendly text
    // — e.g. "Super Admin" — while filtering on the raw stored value — "disclosureAdmin").
    const norm = (options || []).map((o) =>
      o && typeof o === "object" ? { value: o.value, label: o.label ?? o.value } : { value: o, label: o }
    );
    const labelFor = (v) => (norm.find((o) => o.value === v)?.label ?? v);
    const q = search.trim().toLowerCase();
    const shown = q ? norm.filter((o) => String(o.label).toLowerCase().includes(q)) : norm;
    return (
      <Select
        multiple
        displayEmpty
        variant="standard"
        fullWidth
        value={selected}
        onChange={(e) => onChange((e.target.value || []).filter((v) => v !== "__search__"))}
        onClose={() => setSearch("")}
        input={<OutlinedInput sx={{ padding: 0 }} />}
        renderValue={(sel) =>
          sel.length === 0 ? (
            <span style={{ color: theme.palette.custom?.gray || "#999", fontSize: "13px" }}>All</span>
          ) : (
            <span style={{ fontSize: "13px", color: theme.palette.custom?.black || "#222" }}>
              {sel.length === 1 ? labelFor(sel[0]) : `${sel.length} selected`}
            </span>
          )
        }
        sx={{
          fontSize: "13px",
          "& .MuiSelect-select": { padding: "2px 0" },
          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
        }}
        MenuProps={{ autoFocus: false, PaperProps: { style: { maxHeight: 340 } } }}
      >
        {/* Searchable: type to narrow the checklist. Not a selectable option; key events are kept
            from the Select's type-ahead so typing filters instead of jumping between items. */}
        <Box
          sx={{ px: 1, py: 0.5, position: "sticky", top: 0, zIndex: 2, backgroundColor: "background.paper" }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <TextField
            autoFocus
            fullWidth
            variant="standard"
            size="small"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={inputTextSx(theme)}
          />
        </Box>
        {shown.map((opt) => (
          <MenuItem key={opt.value} value={opt.value} dense>
            <Checkbox size="small" checked={selected.indexOf(opt.value) > -1} />
            <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: "13px" }} />
          </MenuItem>
        ))}
        {shown.length === 0 && (
          <MenuItem disabled dense>
            <ListItemText primary="No matches" primaryTypographyProps={{ fontSize: "13px" }} />
          </MenuItem>
        )}
      </Select>
    );
  }

  if (type === "date") {
    const spec = value && typeof value === "object" ? value : { mode: "relative" };
    const active =
      (spec.mode === "relative" && spec.days) ||
      (spec.mode === "range" && (spec.start || spec.end)) ||
      (spec.mode === "exact" && spec.date);
    const setMode = (mode) => onChange({ mode });
    return (
      <>
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <FilterListIcon
            fontSize="small"
            sx={{ color: active ? theme.palette.primary?.main || "#0082E1" : theme.palette.custom?.gray || "#999" }}
          />
        </IconButton>
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Box sx={{ p: 2, width: 260, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 700 }}>Filter by date</Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={spec.mode || "relative"}
              onChange={(e, m) => m && setMode(m)}
              fullWidth
            >
              <ToggleButton value="relative" sx={{ fontSize: "11px" }}>Relative</ToggleButton>
              <ToggleButton value="range" sx={{ fontSize: "11px" }}>Range</ToggleButton>
              <ToggleButton value="exact" sx={{ fontSize: "11px" }}>Exact</ToggleButton>
            </ToggleButtonGroup>

            {spec.mode === "range" ? (
              <>
                <TextField
                  label="From"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={spec.start || ""}
                  onChange={(e) => onChange({ ...spec, start: e.target.value })}
                />
                <TextField
                  label="To"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={spec.end || ""}
                  onChange={(e) => onChange({ ...spec, end: e.target.value })}
                />
              </>
            ) : spec.mode === "exact" ? (
              <TextField
                label="On date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={spec.date || ""}
                onChange={(e) => onChange({ ...spec, date: e.target.value })}
              />
            ) : (
              <Select
                size="small"
                value={spec.days || ""}
                displayEmpty
                onChange={(e) => onChange({ mode: "relative", days: e.target.value })}
              >
                <MenuItem value=""><em>Any time</em></MenuItem>
                <MenuItem value={1}>Last 24 hours</MenuItem>
                <MenuItem value={7}>Last 7 days</MenuItem>
                <MenuItem value={30}>Last 30 days</MenuItem>
                <MenuItem value={90}>Last 90 days</MenuItem>
              </Select>
            )}

            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
              <Button size="small" onClick={() => { onChange({ mode: "relative" }); }}>
                Clear
              </Button>
              <Button size="small" variant="contained" onClick={() => setAnchorEl(null)}>
                Done
              </Button>
            </Box>
          </Box>
        </Popover>
      </>
    );
  }

  // Default: free-text contains filter.
  return (
    <TextField
      variant="standard"
      size="small"
      fullWidth
      placeholder="Filter"
      value={typeof value === "string" ? value : ""}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      sx={inputTextSx(theme)}
    />
  );
};

export default ColumnFilter;
